import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAdminService } from '../services/admin.service';
import { QUERY_KEYS } from '../utils/queryKeys';

/**
 * Custom hook for individual plan queries and mutations
 */
export const useIndividualPlanQueries = (userId: string) => {
    const { getIndividualPlan, setIndividualPlan, deleteIndividualPlan } = useAdminService();
    const queryClient = useQueryClient();

    // Query for getting individual plan
    const individualPlanQuery = useQuery({
        queryKey: [QUERY_KEYS.ADMIN_INDIVIDUAL_PLAN, userId],
        queryFn: () => getIndividualPlan(userId),
        retry: false,
        gcTime: 0,
        staleTime: 0,
    });

    // Mutation for setting/updating individual plan
    const setIndividualPlanMutation = useMutation({
        mutationFn: ({ price }: { price: number }) => setIndividualPlan(userId, price),
        onSuccess: () => {
            // Invalidate and refetch the individual plan query
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ADMIN_INDIVIDUAL_PLAN, userId],
            });
        },
        onError: (error) => {
            console.error('Error setting individual plan:', error);
        },
    });

    // Mutation for deleting individual plan
    const deleteIndividualPlanMutation = useMutation({
        mutationFn: () => deleteIndividualPlan(userId),
        onSuccess: () => {
            // Invalidate and refetch the individual plan query
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ADMIN_INDIVIDUAL_PLAN, userId],
            });
        },
        onError: (error) => {
            console.error('Error deleting individual plan:', error);
        },
    });

    return {
        // Query data and states
        individualPlan: individualPlanQuery.data,
        isLoading: individualPlanQuery.isLoading,
        error: individualPlanQuery.error,
        isError: individualPlanQuery.isError,

        // Mutations
        setIndividualPlan: setIndividualPlanMutation.mutate,
        deleteIndividualPlan: deleteIndividualPlanMutation.mutate,

        // Mutation states
        isSettingPlan: setIndividualPlanMutation.isPending,
        isDeletingPlan: deleteIndividualPlanMutation.isPending,
        setError: setIndividualPlanMutation.error,
        deleteError: deleteIndividualPlanMutation.error,

        // Utility functions
        refetch: individualPlanQuery.refetch,
    };
};
